import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import rootStore from "../../../stores/rootStore.js";
import {scheduledOfflineApi} from "../../../api/scheduledOffline";
import {taxAuthApi} from "../../../api/taxAuth.js";
import {Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table} from "react-bootstrap";
import HeroSection from "../../../components/HeroSection.jsx";

function OutageAdminPage() {
    const {authenticationStore: authStore} = rootStore;
    const [outages, setOutages] = useState([]);
    const [outageStatus, setOutageStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [newOutage, setNewOutage] = useState({
        offlineReason: "",
        offlineStart: "",
        offlineEnd: ""
    });

    const taxAuthorityId = authStore.authentication?.orgId;

    const loadPage = async () => {
        if (!taxAuthorityId) {
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            const [scheduledResponse, unplannedResponse] = await Promise.all([
                scheduledOfflineApi.getScheduledOffline(taxAuthorityId, null, false),
                taxAuthApi.getCurrentOutageStatus()
            ]);
            setOutages(scheduledResponse);
            setOutageStatus(unplannedResponse);
        } catch (loadError) {
            console.error("Failed to load outage administration page", loadError);
            setError("Failed to load outage information.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taxAuthorityId]);

    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setNewOutage({...newOutage, [name]: value});
    };

    const handleAddOutage = async () => {
        await scheduledOfflineApi.addScheduledOffline(newOutage.offlineReason, newOutage.offlineStart, newOutage.offlineEnd, taxAuthorityId, null);
        setNewOutage({
            offlineReason: "",
            offlineStart: "",
            offlineEnd: ""
        });
        await loadPage();
    };

    const handleDeleteOutage = async (outageId) => {
        await scheduledOfflineApi.deleteScheduledOffline(outageId);
        await loadPage();
    };

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="muni"
                eyebrow="Outages"
                title="Outage Administration"
                subtitle="Manage planned downtime and review live unplanned outage status from health checks."
            />

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {isLoading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading outage information...</span>
                </div>
            )}

            {!isLoading && (
                <Row className="g-4 mt-1">
                    <Col xl={6}>
                        <Card className="dashboard-panel h-100">
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                                <strong>Planned Outages</strong>
                                <Badge bg="light" text="dark">{outages.length} Scheduled</Badge>
                            </Card.Header>
                            <Card.Body>
                                <Form>
                                    <Row className="g-3">
                                        <Form.Group as={Col} md={12}>
                                            <Form.Label>Reason</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="offlineReason"
                                                value={newOutage.offlineReason}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Maintenance window"
                                            />
                                        </Form.Group>
                                        <Form.Group as={Col} md={6}>
                                            <Form.Label>Start Time</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                name="offlineStart"
                                                value={newOutage.offlineStart}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                        <Form.Group as={Col} md={6}>
                                            <Form.Label>End Time</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                name="offlineEnd"
                                                value={newOutage.offlineEnd}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Row>
                                    <Button className="mt-3" variant="primary" onClick={handleAddOutage}>
                                        Save Outage
                                    </Button>
                                </Form>
                            </Card.Body>
                            <Card.Body className="border-top p-0">
                                <Table striped bordered hover responsive className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Reason</th>
                                            <th>Start Time</th>
                                            <th>End Time</th>
                                            <th style={{width: 120}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {outages.map((outage) => (
                                            <tr key={outage.id}>
                                                <td>{outage.reason}</td>
                                                <td>{new Date(outage.offlineStart).toLocaleString()}</td>
                                                <td>{new Date(outage.offlineEnd).toLocaleString()}</td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteOutage(outage.id)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {!outages.length && (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted py-3">No outages scheduled.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xl={6}>
                        <Card className="dashboard-panel h-100">
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                                <strong>Unplanned Outages</strong>
                                <Badge bg={outageStatus?.online ? "success" : "danger"}>
                                    {outageStatus?.online ? "Online" : "Offline"}
                                </Badge>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <div className="small text-muted">Current Status</div>
                                    <div className="h5 mb-1">{outageStatus?.online ? "Online" : "Offline"}</div>
                                    <div className="text-muted">{outageStatus?.statusMessage || "No status available."}</div>
                                </div>

                                {!outageStatus?.online && !!outageStatus?.currentIssues?.length && (
                                    <div>
                                        <div className="small text-muted mb-2">Current Issues</div>
                                        <div className="d-flex flex-column gap-2">
                                            {outageStatus.currentIssues.map((issue) => (
                                                <div key={issue.productId} className="border rounded p-2">
                                                    <div><strong>{issue.productName}</strong></div>
                                                    <div className="text-muted small">{issue.offlineDescription}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                            <Card.Body className="border-top p-0">
                                <Table striped bordered hover responsive className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Reason</th>
                                            <th>Offline At</th>
                                            <th>Back Online</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(outageStatus?.auditHistory || []).map((audit) => (
                                            <tr key={audit.id}>
                                                <td>{audit.productName}</td>
                                                <td>{audit.offlineReason}</td>
                                                <td>{new Date(audit.offlineStart).toLocaleString()}</td>
                                                <td>{audit.onlineAt ? new Date(audit.onlineAt).toLocaleString() : "Still offline"}</td>
                                            </tr>
                                        ))}
                                        {!outageStatus?.auditHistory?.length && (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted py-3">No unplanned outages recorded.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    );
}

export default observer(OutageAdminPage);
